import { stagePathConfig } from 'data/stage';

export type BookingStagePath =
  | typeof stagePathConfig.rooms
  | typeof stagePathConfig.traveller
  | typeof stagePathConfig.payment;

// GWAT: Rooms -> Traveller -> Payment.
// DealsAway: Traveller -> Rooms -> Payment, so the lead is captured first.
const GWAT_STAGE_ORDER: BookingStagePath[] = [
  stagePathConfig.rooms,
  stagePathConfig.traveller,
  stagePathConfig.payment,
];

const DEALSAWAY_STAGE_ORDER: BookingStagePath[] = [
  stagePathConfig.traveller,
  stagePathConfig.rooms,
  stagePathConfig.payment,
];

export const getBookingStageOrder = (isDA: boolean): BookingStagePath[] =>
  isDA ? DEALSAWAY_STAGE_ORDER : GWAT_STAGE_ORDER;

/** True when `targetPath` comes before `currentPath` in the tenant's order. */
export const isEarlierBookingStage = (
  isDA: boolean,
  currentPath: string,
  targetPath: string
): boolean => {
  const order: string[] = getBookingStageOrder(isDA);
  const currentIndex = order.indexOf(currentPath);
  const targetIndex = order.indexOf(targetPath);

  return currentIndex > -1 && targetIndex > -1 && targetIndex < currentIndex;
};

type DealsAwayRedirectInput = {
  pathname: string;
  /** Trip has room options and the session is not locked. */
  hasRoomStage: boolean;
  hasSelectedRoom: boolean;
  hasTravellerData: boolean;
  hasDepartureDate: boolean;
};

/**
 * Where a DealsAway visitor should be sent for the given booking path,
 * or `null` when they can stay. Mirrors the GWAT rules in `useNavigator`
 * for the Traveller -> Rooms -> Payment order.
 */
export const getDealsAwayRedirect = ({
  pathname,
  hasRoomStage,
  hasSelectedRoom,
  hasTravellerData,
  hasDepartureDate,
}: DealsAwayRedirectInput): string | null => {
  switch (pathname) {
    case '/':
      if (!hasTravellerData || !hasDepartureDate) {
        return stagePathConfig.traveller;
      }
      if (hasRoomStage && !hasSelectedRoom) {
        return stagePathConfig.rooms;
      }
      return stagePathConfig.payment;

    case stagePathConfig.traveller:
      return null;

    case stagePathConfig.rooms:
      if (!hasRoomStage) return '/';
      if (!hasTravellerData) return stagePathConfig.traveller;
      return null;

    case stagePathConfig.payment:
      if (
        !hasTravellerData ||
        (hasRoomStage && !hasSelectedRoom) ||
        !hasDepartureDate
      ) {
        return '/';
      }
      return null;

    default:
      return null;
  }
};
